import type { KeyboardEvent, ReactNode } from 'react';

import { Icon } from '../Icon.js';

/**
 * A NAVIGATOR BESIDE A DETAIL — the editor layout every long list in the
 * workspace uses (scenes, requirements, conversations).
 *
 * On a desk both panes show: the navigator keeps its own scroll, so working
 * through item 60 of 93 never sends the page back to the top. Below the
 * breakpoint the two take turns — the list, then the item with a way back to
 * the list — because a 390px screen holds one of them well and two of them
 * badly. Which one shows is the caller's state (`showDetail`), so it can live
 * in the address like everything else.
 */
export function SplitWorkspace({
  nav,
  detail,
  showDetail,
  onBackToList,
  backLabel,
  navLabel,
  testId,
}: {
  nav: ReactNode;
  detail: ReactNode;
  /** On a narrow screen: show the detail (true) or the list (false). */
  showDetail: boolean;
  onBackToList: () => void;
  backLabel: string;
  navLabel: string;
  testId?: string;
}) {
  return (
    <div className="ui-split" data-view={showDetail ? 'detail' : 'list'} data-testid={testId}>
      <nav className="ui-split-nav" aria-label={navLabel}>
        {nav}
      </nav>
      <section className="ui-split-detail">
        <button type="button" className="ui-split-back" onClick={onBackToList} data-testid={testId === undefined ? undefined : `${testId}-back`}>
          <Icon name="arrowLeft" size={14} />
          {backLabel}
        </button>
        {detail}
      </section>
    </div>
  );
}

/**
 * Up and down move through a navigator's rows; Home and End go to the ends.
 * The rows are buttons, so Enter and Space already open one — this only adds
 * the movement a long list needs. Focus follows the selection.
 */
export function navigatorKeyDown<T extends string>(
  ids: readonly T[],
  current: T | null,
  select: (id: T) => void,
  rowSelector: (id: T) => string,
): (event: KeyboardEvent<HTMLElement>) => void {
  return (event) => {
    if (ids.length === 0) return;
    const at = current === null ? -1 : ids.indexOf(current);
    let next: number | null = null;
    if (event.key === 'ArrowDown') next = Math.min(ids.length - 1, at + 1);
    else if (event.key === 'ArrowUp') next = Math.max(0, at - 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = ids.length - 1;
    if (next === null) return;

    const id = ids[next];
    if (id === undefined) return;
    event.preventDefault();
    select(id);
    const row = event.currentTarget.querySelector<HTMLElement>(rowSelector(id));
    row?.focus();
  };
}
